
export * from './validators';
export * from './permissions';
import moment from 'moment';

function getDateFormat(): string {
    const settings = JSON.parse(localStorage.getItem('business-settings') || 'null');
    return settings?.dateFormat || 'DD/MM/YYYY';
}

/**
 * A calendar day for a range filter, in the format the API expects: a bare local date with
 * no time and no zone marker (`2026-08-09`).
 *
 * Range bounds are days in the user's calendar, not instants. The server reads a bare date in
 * the caller's timezone (from the `X-Timezone` header) and widens it to the matching UTC range
 * itself — so sending `toISOString()` here converts the day twice and lands on the wrong one.
 *
 * Instants — an invoice date, a payment timestamp — are the other case, and still go over the
 * wire as UTC via `toISOString()`.
 *
 * Accepts a picker `Date` or an ISO instant string; both are read in local time.
 */
export function toDateParam(date: string | Date | null | undefined): string {
    if (date === null || date === undefined || date === '') return '';
    const m = moment(date);
    return m.isValid() ? m.format('YYYY-MM-DD') : '';
}

/** Minutes to add to UTC to reach the browser's local time (+300 for UTC+5). */
export function getTimezoneOffsetMinutes(date: string | Date = new Date()): number {
    return -new Date(date).getTimezoneOffset();
}

/**
 * True for the year-1 / year-9999 markers the API uses to mean "no date".
 *
 * Compared in UTC because that is the zone the markers are exact in — read as local, year 1
 * lands in year 0 west of Greenwich and year 9999 spills into year 10000 east of it.
 */
export function isSentinelDate(value: string | Date | null | undefined): boolean {
    if (value === null || value === undefined || value === '') return true;
    const year = moment.utc(value).year();
    return year <= 1 || year >= 9999;
}

/** IANA timezone name of the browser (e.g. 'Asia/Karachi'), or 'UTC' when unavailable. */
export function getTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Renders an API date in the user's local timezone. The single place that decides how a value
 * off the wire maps to a wall clock — {@link formatDate} and `CustomDatePipe` both defer here.
 *
 * - An instant, with or without a zone marker, is converted to local. A missing marker still
 *   means UTC: that is what the API sends for a `datetime2` column.
 * - A date-only string (`2026-08-09`) is a calendar date, not an instant, so it renders as
 *   written — reading it as UTC midnight would move it to the previous day west of Greenwich.
 */
export function formatLocal(date: string | Date | null | undefined, format: string): string {
    if (date === null || date === undefined || date === '') return '';
    const m = typeof date === 'string' && DATE_ONLY.test(date)
        ? moment(date, 'YYYY-MM-DD')
        : moment.utc(date).local();
    return m.isValid() ? m.format(format) : '';
}

/** {@link formatLocal} in the franchise's configured date format. */
export function formatDate(date: string | Date): string {
    return formatLocal(date, getDateFormat());
}

